import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import TipiAbbonamentoLinea from '../../components/admin/TipiAbbonamentoLinea';
import LocalitaGruppo from '../../components/admin/LocalitaGruppo';
import api from '../../services/api';
import notify from '../../utils/notifications';
import {
  ArrowLeft, Edit, Trash2, MapPin, Plus, GripVertical, Save, Maximize2, Map, X,
  Bus, Calendar, Clock, Users, CheckCircle, XCircle, School, Search
} from 'lucide-react';


const LineaDettaglio = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [linea, setLinea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [abbonamenti, setAbbonamenti] = useState([]);
  const [corse, setCorse] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [showScuoleModal, setShowScuoleModal] = useState(false);
  const [allScuole, setAllScuole] = useState([]);
  const [selectedScuole, setSelectedScuole] = useState([]);
  const [selectedScuoleIds, setSelectedScuoleIds] = useState([]);
  const [loadingScuole, setLoadingScuole] = useState(false);
  const [searchScuola, setSearchScuola] = useState('');
  const [filtroComune, setFiltroComune] = useState('');
  const searchTimeoutRef = useRef(null);
  

  useEffect(() => {
    caricaDatiLinea();
  }, [id]);
  

  const caricaDatiLinea = async () => {
    try {
      setLoading(true);
      
      // Carica linea
      const lineaRes = await api.get(`/trasporti/linee/${id}/`);
      setLinea(lineaRes.data);
      setFormData(lineaRes.data);
      
      // Imposta le scuole selezionate (array di ID)
      setSelectedScuoleIds(lineaRes.data.scuole || []);

      // Carica abbonamenti per questa linea
      const abbRes = await api.get(`/abbonamenti/abbonamenti/?linea=${id}`);
      setAbbonamenti(abbRes.data.results || abbRes.data);

      // Carica corse recenti per questa linea
      const corseRes = await api.get(`/trasporti/corse/?linea=${id}`);
      const corseData = corseRes.data.results || corseRes.data;
      // Ordina per data decrescente e prendi le ultime 10
      setCorse(corseData.sort((a, b) => new Date(b.data) - new Date(a.data)).slice(0, 10));
      
    } catch (error) {
      console.error('Errore caricamento linea:', error);
      notify.error('Errore nel caricamento dei dati della linea');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare questa linea? Verranno eliminati anche tutti gli abbonamenti e corse collegati.')) {
      return;
    }
    
    const toastId = notify.loading('Eliminazione in corso...');
    
    try {
      await api.delete(`/trasporti/linee/${id}/`);
      notify.update(toastId, { type: 'success', message: 'Linea eliminata con successo!' });
      setTimeout(() => navigate('/admin/linee'), 500);
    } catch (error) {
      console.error('Errore eliminazione linea:', error);
      notify.update(toastId, { type: 'error', message: 'Errore nell\'eliminazione della linea' });
    }
  };
  
  const handleUpdate = async (e) => {
    e.preventDefault();
    const toastId = notify.loading('Aggiornamento in corso...');
    
    try {
      await api.put(`/trasporti/linee/${id}/`, formData);
      await caricaDatiLinea();
      setShowEditModal(false);
      notify.update(toastId, { type: 'success', message: 'Linea aggiornata con successo!' });
    } catch (error) {
      console.error('Errore aggiornamento linea:', error);
      notify.update(toastId, { type: 'error', message: 'Errore nell\'aggiornamento della linea' });
    }
  };
  

  // Funzioni per gestire le scuole
  const caricaScuole = async (searchQuery = '') => {
    try {
      setLoadingScuole(true);
      
      const params = {
        q: searchQuery,
        limit: 100
      };
      
      const res = await api.get('/trasporti/scuole/search/', { params });
      setAllScuole(res.data.results || []);
      
      console.log(`✅ Trovate ${res.data.results?.length || 0} scuole per "${searchQuery}"`);
    } catch (error) {
      console.error('Errore caricamento scuole:', error);
      notify.error('Errore nel caricamento delle scuole');
    } finally {
      setLoadingScuole(false);
    }
  };

  const handleSaveScuole = async () => {
    const toastId = notify.loading('Salvataggio scuole...');
    
    try {
      await api.patch(`/trasporti/linee/${id}/`, {
        scuole_servite: JSON.stringify(selectedScuole)
      });
      
      notify.update(toastId, { type: 'success', message: 'Scuole aggiornate con successo!' });
      setShowScuoleModal(false);
      await caricaDatiLinea();
    } catch (error) {
      console.error('Errore salvataggio scuole:', error);
      notify.update(toastId, { type: 'error', message: 'Errore nel salvataggio delle scuole' });
    }
  };

  const handleScuolaToggle = (scuola) => {
    setSelectedScuoleIds(prev => {
      if (prev.includes(scuola.id)) {
        setSelectedScuole(prevScuole => prevScuole.filter(s => s.id !== scuola.id));
        return prev.filter(id => id !== scuola.id);
      } else {
        setSelectedScuole(prevScuole => [...prevScuole, scuola]);
        return [...prev, scuola.id];
      }
    });
  };

  if (loading || !linea) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Caricamento...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="mb-5">
          <button
            onClick={() => navigate('/admin/linee')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna ai gruppi
          </button>
          
          <div className="flex flex-wrap justify-between items-start gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Gruppo {linea.codice} - {linea.nome}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">{linea.descrizione || 'Nessuna descrizione'}</p>
            </div>
            <div className="flex gap-3">
              <span className={`px-4 py-2 text-sm rounded-full font-semibold ${
                linea.attiva ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {linea.attiva ? 'Attiva' : 'Non Attiva'}
              </span>
              <button
                onClick={() => navigate(`/admin/linee/${id}/edit`)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition"
              >
                <Edit className="w-4 h-4" />
                Modifica Info
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                <Trash2 className="w-4 h-4" />
                Elimina
              </button>
            </div>
          </div>
        </div>


        {/* Sezioni Dettagli, Orari, Scuole, ecc. */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          {/* Colonna sinistra (2/3) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Dettagli Gruppo */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Bus className="w-5 h-5 text-blue-600" />
                Dettagli Gruppo
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Codice</p>
                  <p className="font-semibold text-gray-900">{linea.codice}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Nome</p>
                  <p className="font-semibold text-gray-900">{linea.nome}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-sm text-gray-500">Descrizione</p>
                  <p className="text-gray-900">{linea.descrizione || 'Non specificata'}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-sm text-gray-500">Giorni di Servizio</p>
                  <p className="font-semibold text-gray-900">{linea.giorni_servizio || 'Lunedì-Venerdì'}</p>
                </div>
              </div>
            </div>

            {/* Orari */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-green-600" />
                Orari Standard
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="text-sm text-gray-500">Orario Partenza</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {linea.orario_partenza || '--:--'}
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <p className="text-sm text-gray-500">Orario Arrivo</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {linea.orario_arrivo || '--:--'}
                  </p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Gli orari possono variare in base alle corse programmate
              </p>
            </div>

            {/* Scuole Servite con CARD */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <School className="w-5 h-5 text-indigo-600" />
                  Scuole Servite ({(() => {
                    try {
                      const scuoleStr = linea.scuole_servite || '';
                      if (!scuoleStr) return 0;
                      
                      if (scuoleStr.trim().startsWith('[') || scuoleStr.trim().startsWith('{')) {
                        const scuole = JSON.parse(scuoleStr);
                        return Array.isArray(scuole) ? scuole.length : 0;
                      }
                      
                      return scuoleStr.split('\n').filter(s => s.trim()).length;
                    } catch {
                      return 0;
                    }
                  })()})
                </h2>
                <button
                  onClick={() => {
                    setShowScuoleModal(true);
                    // Carica scuole già selezionate
                    try {
                      const scuoleStr = linea.scuole_servite || '';
                      
                      if (scuoleStr.trim().startsWith('[') || scuoleStr.trim().startsWith('{')) {
                        const scuole = JSON.parse(scuoleStr);
                        if (Array.isArray(scuole)) {
                          setSelectedScuoleIds(scuole.map(s => s.id || s.codice_meccanografico));
                          setSelectedScuole(scuole);
                          setAllScuole([]);
                        }
                      } else {
                        setSelectedScuoleIds([]);
                        setSelectedScuole([]);
                        setAllScuole([]);
                      }
                    } catch (e) {
                      console.error('Errore parsing scuole:', e);
                      setSelectedScuoleIds([]);
                      setSelectedScuole([]);
                      setAllScuole([]);
                    }
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition text-sm"
                >
                  <Edit className="w-4 h-4" />
                  Gestisci Scuole
                </button>
              </div>
              
              {(() => {
                try {
                  const scuoleStr = linea.scuole_servite || '';
                  
                  if (!scuoleStr || scuoleStr.trim() === '') {
                    return <p className="text-gray-500 text-center py-4">Nessuna scuola collegata</p>;
                  }
                  
                  if (scuoleStr.trim().startsWith('[') || scuoleStr.trim().startsWith('{')) {
                    const scuole = JSON.parse(scuoleStr);
                    
                    if (!Array.isArray(scuole) || scuole.length === 0) {
                      return <p className="text-gray-500 text-center py-4">Nessuna scuola collegata</p>;
                    }
                    
                    return (
                      <div className="grid grid-cols-1 gap-3">
                        {scuole.map((scuola) => (
                          <div key={scuola.id || scuola.codice_meccanografico} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="font-semibold text-gray-900 mb-1">{scuola.denominazione}</h3>
                                <p className="text-sm text-gray-600">
                                  {scuola.codice_meccanografico}
                                </p>
                                <p className="text-sm text-gray-600 mt-1">
                                  {scuola.indirizzo && `${scuola.indirizzo}, `}
                                  {scuola.comune} ({scuola.provincia})
                                </p>
                              </div>
                              <span className="px-3 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full font-semibold whitespace-nowrap ml-2">
                                {scuola.tipo_grado_istruzione}
                              </span>
                            </div>
                            {scuola.email && (
                              <p className="text-xs text-gray-500 mt-2">
                                📧 {scuola.email}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  }
                  
                  return (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800 mb-2 font-semibold">
                        ⚠️ Formato legacy (testo)
                      </p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{scuoleStr}</p>
                    </div>
                  );
                } catch (e) {
                  return (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm text-red-800">
                        ❌ Errore nel caricamento delle scuole: {e.message}
                      </p>
                    </div>
                  );
                }
              })()}
            </div>

            {/* Tipi Abbonamento */}
            <LocalitaGruppo gruppoId={id} />

            <TipiAbbonamentoLinea lineaId={id} lineaNome={linea.nome} />

            {/* Note */}
            {linea.note && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-purple-600" />
                  Note
                </h2>
                <p className="text-gray-700 whitespace-pre-wrap">{linea.note}</p>
              </div>
            )}

            {/* Abbonamenti */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-green-600" />
                Abbonamenti Attivi ({abbonamenti.filter(a => a.stato === 'ATTIVO').length})
              </h2>
              {abbonamenti.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Nessun abbonamento per questa linea</p>
              ) : (
                <div className="space-y-2">
                  {abbonamenti.slice(0, 5).map((abb) => (
                    <div key={abb.id} className="flex items-center justify-between border-b border-gray-100 pb-2">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{abb.codice}</p>
                        <p className="text-xs text-gray-600">{abb.beneficiario_nome}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
                        abb.stato === 'ATTIVO' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {abb.stato}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Corse Recenti */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Corse Recenti ({corse.length})
              </h2>
              {corse.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Nessuna corsa registrata</p>
              ) : (
                <div className="space-y-2">
                  {corse.map((corsa) => (
                    <div key={corsa.id} className="flex items-center justify-between border-b border-gray-100 pb-2">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {new Date(corsa.data).toLocaleDateString('it-IT')}
                        </p>
                        <p className="text-xs text-gray-600">
                          {corsa.tipo_corsa} - Passeggeri: {corsa.numero_passeggeri || 0}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
                        corsa.stato === 'COMPLETATA' ? 'bg-green-100 text-green-800' :
                        corsa.stato === 'IN_CORSO' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {corsa.stato}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Colonna destra (1/3) - Statistiche */}
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Statistiche</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span>Scuole Servite</span>
                  <span className="text-2xl font-bold text-gray-900">{(() => {
                    try {
                      const scuole = JSON.parse(linea.scuole_servite || '[]');
                      return Array.isArray(scuole) ? scuole.length : 0;
                    } catch {
                      return 0;
                    }
                  })()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Abbonamenti Totali</span>
                  <span className="text-2xl font-bold text-gray-900">{abbonamenti.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Abbonamenti Attivi</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {abbonamenti.filter(a => a.stato === 'ATTIVO').length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Corse Totali</span>
                  <span className="text-2xl font-bold text-gray-900">{corse.length}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                {linea.attiva ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600" />
                )}
                Stato Linea
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Linea</span>
                  <span className={`font-semibold ${linea.attiva ? 'text-green-600' : 'text-red-600'}`}>
                    {linea.attiva ? 'Attiva' : 'Non Attiva'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Servizio</span>
                  <span className="font-semibold text-gray-900">{linea.giorni_servizio}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-600" />
                Info Sistema
              </h3>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-gray-500">Creata il</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(linea.data_creazione).toLocaleDateString('it-IT')}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Ultima modifica</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(linea.data_modifica).toLocaleDateString('it-IT')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Modifica Linea */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Modifica Linea</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleUpdate} className="p-6 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Codice *</label>
                    <input
                      type="text"
                      required
                      value={formData.codice || ''}
                      onChange={(e) => setFormData({ ...formData, codice: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Nome *</label>
                    <input
                      type="text"
                      required
                      value={formData.nome || ''}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Descrizione</label>
                  <textarea
                    value={formData.descrizione || ''}
                    onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Orario Partenza</label>
                    <input
                      type="time"
                      value={formData.orario_partenza || ''}
                      onChange={(e) => setFormData({ ...formData, orario_partenza: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Orario Arrivo</label>
                    <input
                      type="time"
                      value={formData.orario_arrivo || ''}
                      onChange={(e) => setFormData({ ...formData, orario_arrivo: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Giorni di Servizio</label>
                  <input
                    type="text"
                    value={formData.giorni_servizio || ''}
                    onChange={(e) => setFormData({ ...formData, giorni_servizio: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Es: Lunedì-Venerdì"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="attiva"
                    checked={formData.attiva || false}
                    onChange={(e) => setFormData({ ...formData, attiva: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <label htmlFor="attiva" className="text-sm font-semibold text-gray-700">
                    Linea Attiva
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Note</label>
                  <textarea
                    value={formData.note || ''}
                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition font-semibold"
                  >
                    Salva Modifiche
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Gestione Scuole */}
        {showScuoleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <School className="w-6 h-6 text-indigo-600" />
                    Gestisci Scuole Servite
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Seleziona le scuole servite da questa linea
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowScuoleModal(false);
                    setSearchScuola('');
                    setFiltroComune('');
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 sm:p-6">
                {/* Counter */}
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900">
                    <strong>{selectedScuoleIds.length}</strong> scuole selezionate
                    {allScuole.length > 0 && (
                      <span className="text-gray-600 ml-2">
                        ({allScuole.length} risultati ricerca)
                      </span>
                    )}
                  </p>
                </div>

                {/* Barra di ricerca */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Cerca per nome, codice, comune... (min 3 caratteri)"
                      value={searchScuola}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSearchScuola(value);
                        
                        if (searchTimeoutRef.current) {
                          clearTimeout(searchTimeoutRef.current);
                        }
                        
                        if (value.length >= 3) {
                          searchTimeoutRef.current = setTimeout(() => {
                            caricaScuole(value);
                          }, 500);
                        } else if (value.length === 0) {
                          setAllScuole([]);
                          setFiltroComune('');
                        }
                      }}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    {searchScuola && (
                      <button
                        onClick={() => {
                          if (searchTimeoutRef.current) {
                            clearTimeout(searchTimeoutRef.current);
                          }
                          setSearchScuola('');
                          setAllScuole([]);
                          setFiltroComune('');
                        }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  {searchScuola.length > 0 && searchScuola.length < 3 && (
                    <p className="text-xs text-gray-500 mt-2">
                      Digita almeno 3 caratteri per cercare...
                    </p>
                  )}
                </div>

                {/* Filtro per Comune */}
                {allScuole.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Filtra per Comune
                    </label>
                    <select
                      value={filtroComune}
                      onChange={(e) => setFiltroComune(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">Tutti i comuni ({allScuole.length})</option>
                      {(() => {
                        const comuni = [...new Set(allScuole.map(s => s.comune).filter(Boolean))].sort();
                        return comuni.map(comune => {
                          const count = allScuole.filter(s => s.comune === comune).length;
                          return (
                            <option key={comune} value={comune}>
                              {comune} ({count})
                            </option>
                          );
                        });
                      })()}
                    </select>
                    {filtroComune && (
                      <p className="text-xs text-gray-600 mt-1">
                        {allScuole.filter(s => s.comune === filtroComune).length} scuole in {filtroComune}
                      </p>
                    )}
                  </div>
                )}

                {/* Area risultati */}
                {loadingScuole ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="ml-3 text-gray-600">Ricerca in corso...</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {allScuole.length === 0 && searchScuola.length === 0 && (
                      <div className="text-center py-12">
                        <Search className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-600 font-semibold">
                          Cerca una scuola
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                          Digita il nome, codice meccanografico o comune per trovare le scuole
                        </p>
                      </div>
                    )}
                    
                    {allScuole.length === 0 && searchScuola.length >= 3 && !loadingScuole && (
                      <div className="text-center py-12">
                        <Search className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-600">
                          Nessuna scuola trovata per "{searchScuola}"
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                          Prova con altri termini di ricerca
                        </p>
                      </div>
                    )}
                    
                    {(() => {
                      const scuoleFiltrate = filtroComune 
                        ? allScuole.filter(s => s.comune === filtroComune)
                        : allScuole;
                      
                      if (scuoleFiltrate.length === 0 && filtroComune) {
                        return (
                          <div className="text-center py-8">
                            <p className="text-gray-600">
                              Nessuna scuola trovata per il comune "{filtroComune}"
                            </p>
                          </div>
                        );
                      }
                      
                      return scuoleFiltrate.map((scuola) => (
                        <div
                          key={scuola.id}
                          onClick={() => handleScuolaToggle(scuola)}
                          className={`border rounded-lg p-4 cursor-pointer transition ${
                            selectedScuoleIds.includes(scuola.id)
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={selectedScuoleIds.includes(scuola.id)}
                              onChange={() => handleScuolaToggle(scuola)}
                              className="mt-1 w-5 h-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                            />
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h3 className="font-semibold text-gray-900">{scuola.denominazione}</h3>
                                  <p className="text-sm text-gray-600 mt-0.5">
                                    {scuola.codice_meccanografico}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1.5 ml-2 shrink-0">
                                  {/* Statale o paritaria: con lo stesso nome
                                      possono esistere entrambe */}
                                  {scuola.gestione === 'Paritaria' && (
                                    <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full font-semibold whitespace-nowrap">
                                      Paritaria
                                    </span>
                                  )}
                                  {scuola.tipo_grado_istruzione && (
                                    <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full font-semibold whitespace-nowrap">
                                      {scuola.tipo_grado_istruzione}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <p className="text-sm text-gray-600 mt-2">
                                {scuola.indirizzo && `${scuola.indirizzo}, `}
                                {scuola.comune} ({scuola.provincia})
                              </p>
                              {scuola.email && (
                                <p className="text-xs text-gray-500 mt-1">
                                  📧 {scuola.email}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowScuoleModal(false);
                    setSearchScuola('');
                    setFiltroComune('');
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition font-semibold"
                >
                  Annulla
                </button>
                <button
                  onClick={handleSaveScuole}
                  className="px-6 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition font-semibold flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Salva Scuole
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default LineaDettaglio;
